import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { d_tables, d_type_modifier_values, d_types, dReferenceGetDescription, DTable, DTableBackend, DTableColumn, dTableCreateDB, dTableGetGoEntityName, DTableRelation, DType, DTypeModifier } from '../lib/mdl/d';
import { makeActionButton, UIButton } from '../lib/ui/component/button';
import { UIDropdown, UIDropdownItem } from '../lib/ui/component/dropdown';
import { UITextInput } from '../lib/ui/component/input';
import { UIBaseInput, UIBaseInputOptions } from '../lib/ui/component/input-base';
import { makeInputWrapper, UIAnyInputWrapper } from '../lib/ui/component/input-wrapper';
import { UILabeled } from '../lib/ui/component/labeled';
import { UISelect } from '../lib/ui/component/select';
import { UITable, UITableDefinition, UITableRowController } from '../lib/ui/component/table';
import { UITabs } from '../lib/ui/component/tabs';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox, UIMultiToggles, UIToggle } from '../lib/ui/component/toggle';
import { $el } from '../lib/ui/elements';
import { uiSetPrefixedClass } from '../lib/ui/functions';
import { UIMenu, uiShowMenu } from '../lib/ui/menu';
import { UIScreen } from '../lib/ui/screen';
import { UIRelPos, UIValue } from '../lib/ui/types';
import { UIView, UIViewAddSpec, UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource, YInputDataSource } from '../lib/y/data-source';
import { $log } from '../lib/y/debug';
import { $on, $trigger } from '../lib/y/events';
import { $form, YForm } from '../lib/y/form';
import { $location } from '../lib/y/location';
import { yCopy, yLogDiff } from '../lib/y/object';
import { YId } from '../lib/y/types';

const referenceTableGenerator = (v: UIValue<YId | undefined>): UIDropdownItem => {
    const view = $el.SPAN('ui-dropdown-item', v.name);

    if(v.key) {
        view.classList.add('m-sub');
        view.ui_add($el.ICON(UIIcon.ANGLE_RIGHT, 'r-sub'));
    }

    return { view: view, search: v.name };
}

const referenceColumnGenerator = (v: DTableColumn): UIDropdownItem => {
    return { view: $el.SPAN('ui-dropdown-item', v.name), search: v.name };
}

const modifierDisplay = (v: DTypeModifier | null | undefined): UIViewAddSpec => {
    if(v == 'array') {
        return $el.DIV('o-data-type-icon m-pkey', '[]');
    } else if(v == 'flags') {
        return $el.DIV('o-data-type-icon m-pkey', '&');
    } else {
        return '\u00a0';
    }
}

interface DTableReference {
    column_id: YId;
    relation: DTableRelation;
    doc_only: boolean;
}

class ReferenceSelect extends UIBaseInput<DTableReference | undefined> {
    private _all_table_map: Record<YId, DTable>
    private _all_column_map: Record<YId, DTableColumn>;
    private _value: DTableReference | undefined;
    private _values: UIValue<YId | undefined>[];
    private _search_input: UIAnyInputWrapper;
    private _display_elem: HTMLElement;
    private _open_dropdown?: UIDropdown;
    private _owned_by: UIToggle;
    private _doc_only: UIToggle;

    constructor(values: UIValue<YId | undefined>[], all_table_map: Record<YId, DTable>, all_column_map: Record<YId, DTableColumn>, options: UIBaseInputOptions = {}) {
        options.r_deco = $el.ICON(UIIcon.ANGLE_DOWN, 'r-arrow');

        const search_input = makeInputWrapper({});
        const alt_input = $el.DIV();
        const input = search_input.element;

        super(options, input, alt_input);
        this._all_table_map = all_table_map;
        this._all_column_map = all_column_map;
        this._values = values;
        this._search_input = search_input;
        this._display_elem = alt_input;
        this.classList.add('ui-select');
        input.tabIndex = 0;

        const owned_by = this._owned_by = new UIToggle($el.ICON(UIIcon.TRASH_CAN, ['o-data-type-icon o-data-toggle', 'm-pkey', 'u-hidden']));
        const doc_only = this._doc_only = new UIToggle($el.ICON(UIIcon.NOTEBOOK, ['o-data-type-icon o-data-toggle', 'm-pkey', 'u-hidden']));
        this.ui_add([owned_by, doc_only]);

        let has_input_focus: boolean = false;
        let has_alt_focus: boolean = false;

        alt_input.tabIndex = 0;
        this.classList.add('f-alt-input');
        this.classList.add('m-fixed-height');

        this.value = undefined;

        alt_input.addEventListener('focus', () => {
            has_alt_focus = true;
            if(!has_input_focus) {
                this.classList.add('f-focus');
                $trigger(this, 'focus');
            }
        });

        alt_input.addEventListener('blur', () => {
            has_alt_focus = false;
            if(!has_input_focus) {
                this.classList.remove('f-focus');
                $trigger(this, 'blur');
            }
        })

        input.addEventListener('focus', () => {
            this.classList.remove('f-alt-input');
            has_input_focus = true;
            if(!has_alt_focus) {
                this.classList.add('f-focus');
                $trigger(this, 'focus');
            }
        });

        input.addEventListener('blur', () => {
            this.classList.add('f-alt-input');
            has_input_focus = false;
            if(!has_alt_focus) {
                this.classList.remove('f-focus');
                $trigger(this, 'blur');
            }

            if(this._open_dropdown) {
                this._open_dropdown.close();
                this._open_dropdown = undefined;
            }
        });

        $on(owned_by, 'value', v => {
            if(this._value) {
                const new_relation = v ? 'owned_by' : 'references';
                if(new_relation != this._value.relation) {
                    this._value.relation = new_relation;
                    $trigger(this, 'value', this._value);
                }
            }
        });

        $on(doc_only, 'value', v => {
            if(this._value) {
                if(v != this._value.doc_only) {
                    this._value.doc_only = v;
                    $trigger(this, 'value', this._value);
                }
            }
        });

        this.root.addEventListener('click', () => { this.showDropdown(); });
    }

    showDropdown(where?: UIViewSpec) {
        if(this.disabled) return;

        this._search_input.text = '';

        this.classList.remove('f-alt-input');
        this.input_elem.focus();

        const on_close = () => {
            this._open_dropdown = undefined;
            if(this._search_input) {
                this.classList.add('f-alt-input');
                this._display_elem.focus();
                this.input_elem.blur();
            }
        }

        const menu = new UIDropdown(this._values, referenceTableGenerator, { search_input: this._search_input });
        menu.classList.add('o-data-menu');
        this._open_dropdown = menu;

        menu.on_choose = (model, view) => {
            $log('app', 'on_choose', model);
            if(model.key) {
                const table = this._all_table_map[model.key];
                const submenu = new UIDropdown(table.columns, referenceColumnGenerator);
                submenu.classList.add('o-data-menu');
                submenu.on_choose = m => {
                    this.value = { column_id: m.id, relation: 'references', doc_only: false }
                };
                submenu.open(view, { position: UIRelPos.Right | UIRelPos.Start, capture_mouse: true, can_flip: true, prevent_blur: true, on_close });
                return false;
            } else {
                this.setDirty(true);
                this.value = undefined;
                return;
            }
        };

        menu.open(where || this, { position: UIRelPos.Below | UIRelPos.End, capture_mouse: true, match_size: true, adjust_size: true, can_flip: true, prevent_blur: true, on_close });
    }

    protected fixText() {
        const v = this._value;

        this._owned_by.classList.toggle('u-hidden', !v);
        this._doc_only.classList.toggle('u-hidden', !v);

        if(!v) {
            this._display_elem.ui_empty('\u00a0');
            return;
        }

        $log('app', 'value', v);
        $log('app', 'all_tables', this._all_table_map);

        const column = this._all_column_map[v.column_id];
        const table = this._all_table_map[column.owner_table_id];
        this._display_elem.ui_empty(table.name + '.' + column.name);
    }

    override focus() {
        this.classList.remove('f-alt-input');
        this.input_elem.focus();
    }

    protected doCheck() {
        this.setOkay(!this.required || !this.is_empty);
    }

    get value(): DTableReference | undefined {
        return this._value;
    }

    set value(value: DTableReference | undefined) {
        this._value = value;
        this._owned_by.value = value?.relation === 'owned_by';
        this._doc_only.value = value?.doc_only || false;

        this.fixText();
        this.doCheck();
        this.fixDesign();

        $trigger(this, 'value', this._value);
    }

    get is_empty() {
        return this._value === undefined;
    }
}


interface TableColumnRowController extends UITableRowController<DTableColumn> {
    model: DTableColumn;
    icon: HTMLElement;
    type_id: UISelect<YId>;
    modifier: UISelect<DTypeModifier | null>
    name: UITextInput;
    is_nullable: UIToggle;
    is_unique: UIToggle;
    is_primary_key: UIToggle;
    references: ReferenceSelect,
    db_default: UITextInput;
    db_codec: UITextInput;
    db_index: UITextInput;

    form: YForm;
}

function tableColumnRowCommit(this: TableColumnRowController) {
    Object.assign(this.model, this.form.value);
    const ref_value = this.references.value;
    this.model.references_column_id = ref_value?.column_id;
    this.model.references_relation = ref_value?.relation;
    this.model.references_doc_only = ref_value?.doc_only || false;
}

class TableStructureTab extends UIView {
    constructor(all_table_map: Record<YId, DTable>, all_column_map: Record<YId, DTableColumn>, all_type_map: Record<YId, DType>, backend: DTableBackend, table?: DTable) {
        super('div', 'tl-admin-page');

        const type_values: UIValue<YId>[] = Object.entries(all_type_map).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(e => ({ key: e[0], name: e[1].name }));
        const table_values: UIValue<YId | undefined>[] = Object.entries(all_table_map).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(e => ({ key: e[0], name: e[1].name }));
        table_values.unshift({ key: undefined, name: 'No reference' });

        const name = new UITextInput({ required: true });
        const entity_name = new UITextInput({ required: true });

        const partition_by = new UITextInput;

        const columns = new YInputDataSource<DTableColumn>();
        const columns_add = new UIButton({ title: '+ Add column', kind: 'k-text', on_click: () => { columns.push({} as DTableColumn) } });

        const form = $form({ name, entity_name, columns });

        if(backend == 'ch') {
            form.add({ db_properties: $form({ partition_by }) })
        }

        const columns_def = new UITableDefinition<DTableColumn, TableColumnRowController>();
        columns_def.row_classes = 'tl-admin-table-row'; // check if we really need the tl-admin-table-row

        columns_def.addCustom({
            title: 'Name', width: -200, classes: 'o-data-input-cell', decorator: (td, row) => {
                td.ui_add(row.controller!.name);
            }
        });
        columns_def.addCustom({
            width: -32, classes: 'o-data-icon-cell', decorator: (td, row) => {
                td.ui_add(row.controller!.icon);
            }
        });
        columns_def.addCustom({
            title: 'Type', width: -180, classes: 'o-data-input-cell', decorator: (td, row) => {
                td.ui_add(row.controller!.type_id);
            }
        });
        columns_def.addCustom({
            width: -60, classes: 'o-data-input-cell', decorator: (td, row) => {
                td.ui_add(row.controller!.modifier);
            }
        });
        columns_def.addCustom({
            width: -132, decorator: (td, row) => {
                td.ui_add($el.DIV('o-data-toggles', [
                    row.controller!.is_primary_key,
                    (backend == 'pg') && row.controller!.is_unique,
                    row.controller!.is_nullable,
                ]));
            }
        });
        if(backend == 'pg') {
            columns_def.addCustom({
                title: 'References', width: -220, classes: 'o-data-input-cell', decorator: (td, row) => {
                    td.ui_add(row.controller!.references);
                }
            });
        }
        columns_def.addCustom({
            title: 'DB Default', width: -160, classes: 'o-data-input-cell', decorator: (td, row) => {
                td.ui_add(row.controller!.db_default);
            }
        });
        if(backend == 'ch') {
            columns_def.addCustom({
                title: 'Codec', width: -160, classes: 'o-data-input-cell', decorator: (td, row) => {
                    td.ui_add(row.controller!.db_codec);
                }
            });
            columns_def.addCustom({
                title: 'Index', width: -200, classes: 'o-data-input-cell', decorator: (td, row) => {
                    td.ui_add(row.controller!.db_index);
                }
            });
        }
        columns_def.addButtons([{
            icon: UIIcon.PLUS, tooltip: 'Insert before', action: item => {
                columns.insertBefore({} as DTableColumn, item);
            }
        }, {
            icon: UIIcon.TRASH_CAN, tooltip: 'Remove', action: item => {
                columns.remove(item);
            }
        }], { width: -62 });

        columns_def.controller = row => {
            const icon = $el.DIV('o-data-type-icon');

            const type_id = new UISelect(type_values, { required: true, kind: 'k-flat', menu_kind: 'o-data-menu' });
            const modifier = new UISelect(d_type_modifier_values, { kind: 'k-flat', menu_kind: 'o-data-menu', classes: 'm-fixed-height', display: modifierDisplay });
            const name = new UITextInput({ required: true, kind: 'k-flat' });
            const is_primary_key = new UIToggle($el.ICON(UIIcon.KEY_SKELETON, ['o-data-type-icon o-data-toggle', 'm-pkey']));
            const is_unique = new UIToggle($el.ICON(UIIcon.ASTERISK, ['o-data-type-icon o-data-toggle', 'm-pkey']));
            const is_nullable = new UIToggle($el.ICON(UIIcon.QUESTION, ['o-data-type-icon o-data-toggle', 'm-pkey']));
            const references = new ReferenceSelect(table_values, all_table_map, all_column_map, { kind: 'k-flat' });
            const db_default = new UITextInput({ kind: 'k-flat', undefined_if_empty: true });
            const db_codec = new UITextInput({ kind: 'k-flat', undefined_if_empty: true });
            const db_index = new UITextInput({ kind: 'k-flat', undefined_if_empty: true });

            $on(type_id, 'value', v => {
                if(v) {
                    const t = all_type_map[v];
                    if(t) {
                        const kind = t.kind;
                        uiSetPrefixedClass(icon, 'm', kind);
                        icon.textContent = kind.slice(0, 1);
                        icon.ui_show();
                        return;
                    }
                }

                icon.ui_hide();
            })

            const db_properties = $form({ default: db_default });
            const form = $form({ _: [references], type_id, modifier, name, is_primary_key, is_nullable, is_unique, references, db_properties });

            if(backend == 'ch') {
                db_properties.add({ codec: db_codec, index: db_index });
            }

            form.value = row.item;
            references.value = row.item.references_column_id ? { column_id: row.item.references_column_id, relation: row.item.references_relation!, doc_only: row.item.references_doc_only } : undefined;

            return {
                model: row.item,
                icon,
                type_id,
                modifier,
                name,
                is_primary_key,
                is_unique,
                is_nullable,
                references,
                db_default,
                db_codec,
                db_index,
                form,
                commit: tableColumnRowCommit
            };
        };

        const section = $el.FORM('ui-section w-block-1200 l-form', [
            backend,
            $el.DIV('s-tiny'),
            $el.DIV('l-input-row', [
                new UILabeled(name, { above: 'Table name', required: true }),
                new UILabeled(entity_name, { above: 'Entity name', required: true }),
            ]),
            (backend == 'ch') && $el.DIV('l-input-row', [
                new UILabeled(partition_by, { above: 'Partition By', required: true }),
            ]),
            new UITable(columns_def, columns, { classes: 'b b-content' }),
            columns_add,
        ]);

        if(table) {
            form.value = yCopy(table);
        }

        this.ui_add([
            section,
            makeActionButton(UIIcon.THUMBS_UP, table ? 'Update' : 'Create', () => {
                if(!form.validate()) return;

                const final_value = { ...(table || { backend }), ...form.value };

                yLogDiff(table || {}, final_value);
                $log('app', 'final value', final_value);

                d_tables.upsert(table?.id, final_value).then(() => {
                    $location.pop();
                }, uiToast);
            })
        ]);
    }
}

class TableInterfaceTab extends UIView {
    constructor(_all_table_map: Record<YId, DTable>, _all_type_map: Record<YId, DType>, table: DTable) {
        super('div', 'tl-admin-page');

        const get_all = new UICheckbox({ title: 'GetAll', undefined_if_empty: true });
        const get_all_keys = new UICheckbox({ title: 'GetAllKeys', undefined_if_empty: true });
        const count_all = new UICheckbox({ title: 'CountAll', undefined_if_empty: true });
        const get = new UICheckbox({ title: 'Get', undefined_if_empty: true });
        const get_many = new UICheckbox({ title: 'GetMany', undefined_if_empty: true });
        const delete_ = new UICheckbox({ title: 'Delete', undefined_if_empty: true });
        const insert = new UICheckbox({ title: 'Insert', undefined_if_empty: true });
        const insert_get = new UICheckbox({ title: 'InsertGet', undefined_if_empty: true });
        const insert_many = new UICheckbox({ title: 'InsertMany', undefined_if_empty: true });
        const upsert = new UICheckbox({ title: 'Upsert', undefined_if_empty: true });
        const update = new UICheckbox({ title: 'Update', undefined_if_empty: true });
        const update_get = new UICheckbox({ title: 'UpdateGet', undefined_if_empty: true });
        const update_many = new UICheckbox({ title: 'UpdateMany', undefined_if_empty: true });
        const replace = new UICheckbox({ title: 'Replace', undefined_if_empty: true });

        const update_columns = new UIMultiToggles(table.columns, col => new UICheckbox({ title: col.name, key: col.id }));
        const iface_by = new UIMultiToggles(table.columns, col => new UICheckbox({ title: col.name, key: col.id }));

        const form = $form({ _: [update_columns, iface_by], iface: $form({ get_all, get_all_keys, count_all, get, get_many, 'delete': delete_, insert, insert_get, insert_many, upsert, update, update_get, update_many, replace }) });

        const section = $el.FORM('ui-section w-block-1200 l-form', [
            $el.DIV('l-toggle-row', [
                get_all,
                get_all_keys,
                count_all
            ]),
            $el.DIV('l-toggle-row', [
                get,
                get_many,
            ]),
            delete_,
            $el.DIV('l-toggle-row', [
                insert,
                insert_get,
                insert_many,
            ]),
            upsert,
            $el.DIV('l-toggle-row', [
                update,
                update_get,
                update_many,
            ]),
            replace,
            new UILabeled(update_columns, { above: 'Update columns' }),
            new UILabeled(iface_by, { above: 'Interface By' }),
        ]);

        form.value = table;
        update_columns.value = table.columns.filter(c => c.is_in_update).map(c => c.id);
        iface_by.value = table.columns.filter(c => c.iface_by).map(c => c.id);

        this.ui_add([
            section,
            makeActionButton(UIIcon.THUMBS_UP, 'Update', () => {
                if(!form.validate()) return;

                const old_table = yCopy(table);

                const is_in_update_value = update_columns.value;
                const iface_by_value = iface_by.value;

                for(const col of table.columns) {
                    col.is_in_update = is_in_update_value.includes(col.id);
                    col.iface_by = iface_by_value.includes(col.id);
                }

                const final_value = { ...table, ...form.value };

                yLogDiff(old_table, final_value);
                $log('app', 'final value', final_value);

                d_tables.update(table.id, final_value).then(() => {
                    $location.pop();
                }, uiToast);
            })
        ]);
    }
}

export class AdminTableScreen extends UIScreen {
    constructor(private _table_map: Record<YId, DTable>, private _column_map: Record<YId, DTableColumn>, private _type_map: Record<YId, DType>, private _table_backend: DTableBackend, private _table?: DTable) {
        super();
        this.title = _table?.name || 'New table';
    }

    protected override fillContent(content: UIViewSpec): void {
        content.classList.add('tl-tabs');

        const table = this._table;

        content.ui_add(new UITabs(table ? [
            { title: 'Structure', view: () => new TableStructureTab(this._table_map, this._column_map, this._type_map, table.backend, table) },
            { title: 'Interface', view: () => new TableInterfaceTab(this._table_map, this._type_map, table) },
        ] : [
            { title: 'Structure', view: () => new TableStructureTab(this._table_map, this._column_map, this._type_map, this._table_backend) },
        ]));
    }
}

export class AdminTablesScreen extends UIScreen {
    private _source!: YArrayDataSource<DTable>;
    private _all_tables!: DTable[];
    private _all_table_map!: Record<YId, DTable>;
    private _all_type_map!: Record<YId, DType>;
    private _all_column_map!: Record<YId, DTableColumn>;

    constructor() {
        super();
    }

    protected override async fillContent(content: UIViewSpec) {
        this._all_tables = await d_tables.getAll();
        this._all_table_map = Object.fromEntries(this._all_tables.map(t => [t.id, t]));
        this._all_type_map = Object.fromEntries((await d_types.getAll()).map(t => [t.id, t]));
        this._all_column_map = Object.fromEntries(this._all_tables.flatMap(t => t.columns).map(c => [c.id, c]));

        const doSearch = () => this._doSearch();

        const filter_bar = new ABFilterBar({ on_search: doSearch, on_filter: doSearch });

        // filters.addCustom('event', $i18n.pr.filter_events, all_events.map(e => ({ key: e.model.id, name: e.name })).sort((a, b) => $fn.ICOMPARE(a.name, b.name)), $i18n.pr.filter_events_all);
        // filters.addCustom('venue', $i18n.pr.filter_venue, all_venues.map(v => ({ key: v.model.id, name: v.model.name })).sort((a, b) => $fn.ICOMPARE(a.name, b.name)), $i18n.pr.filter_venues_all);
        // // filters.addCustom('city', $i18n.pr.filter_city, all_cities.map(c => ({ key: c.id, name: c.name })).sort((a, b) => $fn.ICOMPARE(a.name, b.name)), $i18n.pr.filter_cities_all);
        // filters.addDateRange('date', $i18n.pr.filter_time_period);

        const def = new UITableDefinition<DTable>();
        def.row_classes = 'tl-admin-table-row'; // check if we really need the tl-admin-table-row

        def.addCustom({
            width: -24, classes: 'tl-thumb-cell', decorator: (td, row) => {
                const backend = row.item.backend;
                td.append($el.DIV(['o-data-type-icon', 'm-' + backend], backend));
            }, comparator: (lhs, rhs, order) => lhs.backend.localeCompare(rhs.backend) * order
        });

        def.addValue(t => t.name, { title: 'Table name', width: 125 });
        def.addValue(t => dTableGetGoEntityName(t), { title: 'Go type', width: 125 });
        def.addValue(t => dReferenceGetDescription(this._all_table_map, this._all_column_map, t), { title: 'References', width: 200 });
        // def.addValue(t => t.kind, { title: 'Kind', width: 125 });

        def.addButtons([
            {
                icon: UIIcon.DATABASE, tooltip: 'Create DB table', action: item => {
                    return dTableCreateDB(item.id).catch(uiToast);
                }
            }, {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => {
                    $location.push({ transient: new AdminTableScreen(this._all_table_map, this._all_column_map, this._all_type_map, item.backend, item) });
                }
            }
        ], { width: -62 });

        // def.addButtons([
        //     { icon: UIIcon.LIST_UL, tooltip: 'Booking list', action: item => {
        //         // $location.push('event_bookings', { event: item[0].model.id, session: item[1].id });
        //     }},
        // ], { width: -31 });
        def.preference_prefix = 'd_tables';

        content.ui_add([
            $el.SECTION('', $el.DIV('tl-new-list-block', [
                filter_bar,
                new UITable(def, this._source = new YArrayDataSource),
            ])),
            makeActionButton(UIIcon.PLUS, 'Create new', b => {
                const menu = new UIMenu([
                    {
                        title: "Clickhouse", action: () => {
                            $location.push({ transient: new AdminTableScreen(this._all_table_map, this._all_column_map, this._all_type_map, 'ch') });
                        }
                    },
                    {
                        title: "PostgreSQL", action: () => {
                            $location.push({ transient: new AdminTableScreen(this._all_table_map, this._all_column_map, this._all_type_map, 'pg') });
                        }
                    },
                ]);

                uiShowMenu(menu, b);

            })
        ]);

        doSearch();
    }

    private _doSearch() {
        // const matcher = this.filter_matcher;
        const tables = this._all_tables
        this._source.data = tables;
    }

    override onViewDidReappear() {
        d_tables.getAll().then(tables => {
            this._all_tables = tables;
            this._all_table_map = Object.fromEntries(tables.map(t => [t.id, t]));
            this._doSearch();
        });
    }
}
